import { createSlice } from '@reduxjs/toolkit';

import { timeInMs } from '../../../../shared';
import cachedFetch from '../../../../shared/cachedFetch';

import { StoryMessage, StoryResponse, StoryState } from './types';
import { getCurrentDayTS, getDaysOfUsage, isWithinOffset } from './utils';
import { GlobalState, Thunk } from '../../../types.global';
import { browserStorageUpdate } from '../../../store/actions';
import { selectFeatureFlags } from '../../../store/selectors';

const initialStoryState: StoryState = {
  queue: [],
  shown: { message: null, visits: null },
  shownMessagesIds: [],
  lastDisplayedByGroupId: {}
};

const processMessages = (
  { messages, config }: StoryResponse,
  { totalCounter, user, story, ui }: GlobalState
): {
  queue: StoryMessage[],
  shownMessagesIds: string[],
} => {
  const isFocus = ui.mode === 'focus';

  const { shells, installationDate } = user;
  const { lastSeen } = totalCounter;
  const { shown } = story;
  let { shownMessagesIds } = story;
  shownMessagesIds = shownMessagesIds || [];

  const focusIds = messages
    .filter(({ trigger }) => trigger[0].type === 'focus')
    .map(({ id }) => id);

  const allFocusUsed = !focusIds
    .find((id) => !shownMessagesIds.includes(id));

  // reset focus IDs messages
  if (allFocusUsed) {
    shownMessagesIds = shownMessagesIds.filter((id) => !focusIds.includes(id));
  }

  const selectedMessages = messages
    .filter(({ id }) => !shownMessagesIds.includes(id))
    .filter(({ trigger, groupId }) =>
      trigger.find(({ type, value }) => {
        if (isFocus) {
          return type === 'focus';
        }

        if (type === 'userCounter') {
          return value === Math.floor(shells.shellsCounter / 100);
        }

        if (type === 'totalCounter') {
          return isWithinOffset(
            value as number,
            lastSeen.total,
            config?.totalCounterOffset || 100
          );
        }

        if (type === 'daysUsed') {
          return value === getDaysOfUsage(installationDate);
        }

        if (type === 'daysPassed' && shown?.message?.groupId !== groupId) {
          return value <= getDaysOfUsage(story.lastDisplayedByGroupId?.[groupId] || installationDate);
        }

        if (type === 'date') {
          return new Date(value).setHours(0, 0, 0, 0) === getCurrentDayTS();
        }
      })
    )
    // only one daysPassed
    .filter((msg, index, arr) => msg.trigger[0].type === 'daysPassed' ?
      (arr.findIndex(({ trigger }) => trigger[0].type === 'daysPassed') === index) :
      true
    )
    .sort((a, b) => b.priority - a.priority);

  return {
    queue: selectedMessages,
    shownMessagesIds,
  }
};

export const getInitialStoryState = (state: {
  story: StoryState;
  user: { ntpVisitCounter: number };
}): StoryState => {
  const { user } = state;
  const story = {
    ...state.story,
    lastDisplayedByGroupId: state.story?.lastDisplayedByGroupId || {},
  };

  if (!story) return initialStoryState;

  const visitsExceeded =
    user?.ntpVisitCounter >=
    story.shown?.visits + (story.shown?.message?.numOfTabs || 3);

  if (
    story.queue?.length &&
    (!story.shown || !story.shown.message || visitsExceeded)
  ) {
    const [message, ...queue] = story.queue;

    return {
      ...story,
      shownMessagesIds: [...(story.shownMessagesIds || []), message.id],
      queue,
      shown: {
        message,
        visits: user?.ntpVisitCounter,
      },
      lastDisplayedByGroupId: (() => {
        const lastDisplayedByGroupId = story.lastDisplayedByGroupId || {};
        const [trigger] = message.trigger;
        if (visitsExceeded && trigger.type === 'daysPassed') {
          lastDisplayedByGroupId[message.groupId] = Date.now();
        }

        return lastDisplayedByGroupId;
      })(),
    };
  } else if (!story.queue?.length && story.shown?.message && visitsExceeded) {
    return {
      ...story,
      shown: { message: null, visits: null },
    };
  }

  return story;
};

const storySlice = createSlice({
  name: 'story',
  initialState: initialStoryState,
  reducers: {
    fetchMessagesFulfilled: (state, { payload }) => {
      state.shown = payload.shown || state.shown;
      state.queue = payload.queue || [];
      state.shownMessagesIds = payload.shownMessagesIds || [];
    },
  },
  extraReducers: {
    [browserStorageUpdate.toString()]: (state, { payload }) => {
      if (payload.isNewer) {
        return getInitialStoryState(payload.state);
      }
    },
  }
});

export const selectShowFireforks = ({
  story,
  user,
}: {
  story: StoryState;
  user: { ntpVisitCounter: number };
}): boolean => {
  const numOfTabs = story.shown?.message?.numOfTabs || 3;
  const finalCount = story.shown?.visits + numOfTabs;
  return Math.abs(user?.ntpVisitCounter - finalCount) + 1 === numOfTabs;
};

export const selectMessageShown = ({
  story,
}: {
  story: StoryState;
}): {
  message: StoryMessage;
  visits: number;
} => {
  return story.shown;

  // return {
  //   message: {
  //     "trigger": [{ "type": "daysUsed", "value": 1 }],
  //     "type": "video",
  //     "id": "doodle_5",
  //     "coverSrc": "doodle/doodle_5.png",
  //     "videoUrl": "https://www.youtube.com/embed/Echd9jXbOJM",
  //     "buttonStyles": { "background": "#1A1AFF" },
  //     "numOfTabs": 15,
  //     "priority": 999999
  //
  //     // trigger: [{ type: 'daysUsed', value: 1 }],
  //     // type: 'video',
  //     // id: 'doodle_1',
  //     // coverSrc: 'doodle/doodle_1.png',
  //     // videoUrl: 'https://www.youtube.com/embed/Echd9jXbOJM',
  //     // buttonStyles: { background: '#216FFB' },
  //     // text: null,
  //     // numOfTabs: 15,
  //     // priority: 999999
  //   },
  //   visits: 5,
  // };
};


export const fetchMessages = (shouldRefresh = false): Thunk => async (dispatch, getState) => {
  const { STORY_MESSAGES } = selectFeatureFlags(getState());

  try {
    // 'https://assets.oceanhero.today/extension/extension-story/messages-backup.json',
    // const filename = STORY_MESSAGES || `messages${
    //   process.env.APP_TYPE === 'lite' ? '-ntp' : ''
    // }.json`;

    // const filename = STORY_MESSAGES || `messages-backup.json`;
    const filename = STORY_MESSAGES;

    if (!filename) return null;

    const storyJSON = await cachedFetch<StoryResponse>(
      `https://oceanhero.today/extension/story/${filename}`,
      timeInMs.hour
    );

    const state = getState();
    if (shouldRefresh) {
      const story = processMessages(storyJSON, {
        ...state,
        story: {
          ...initialStoryState,
          shownMessagesIds: state.story.shownMessagesIds,
        }
      });

      const newState = getInitialStoryState({
        ...state,
        story: { ...initialStoryState, ...story }
      });
      // console.log(newState);

      dispatch(storySlice.actions.fetchMessagesFulfilled(newState));
    } else {
      const result = processMessages(storyJSON, getState());
      dispatch(storySlice.actions.fetchMessagesFulfilled(result));
    }
  } catch (err) {
    console.error('fetchMessages', err);
    return null;
  }
};

export default storySlice.reducer;

